import { useCallback, useState } from "react";
import apiClient from "../api/apiClient";

export type CompetencyType =
  | "SELF_AWARENESS"
  | "SELF_MANAGEMENT"
  | "SOCIAL_AWARENESS"
  | "RELATIONSHIP_SKILLS"
  | "RESPONSIBLE_DECISION_MAKING";

// Pulse item type
interface Pulse {
  competency: CompetencyType;
  S: number;
  W?: number;
  I?: number;
  E?: number;
}

interface CaptureSelPayload {
  userId: string;
  schoolHash: string;
  classId: string;
  source_type?: string;
  pulses: Pulse[];
}

interface UseCaptureSelReturn {
  loading: boolean;
  error: string | null;
  success: boolean;
  captureSel: (payload: CaptureSelPayload) => Promise<void>;
  reset: () => void;
}

const useCaptureSelPulse = (): UseCaptureSelReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const captureSel = useCallback(
    async (payload: CaptureSelPayload) => {
      if (loading) return; // Prevent double click
      if (!payload.userId) return; // userId not yet resolved — skip API call

      try {
        setLoading(true);
        setError(null);
        setSuccess(false);

        await apiClient.post("/captureSELPulse", {
          userId: payload.userId,
          schoolHash: payload.schoolHash,
          classId: payload.classId,
          source_type: payload.source_type || "Adult",
          pulses: payload.pulses.map((pulse) => ({
            ...pulse,
            W: pulse.W ?? 0.7,
            I: pulse.I ?? 1.0,
            E: pulse.E ?? 1.0,
          })),
        });

        setSuccess(true);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Failed to capture SEL pulse");
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  const reset = () => {
    setError(null);
    setSuccess(false);
    setLoading(false);
  };

  return {
    loading,
    error,
    success,
    captureSel,
    reset,
  };
};

export default useCaptureSelPulse;
